import {z} from 'zod'
import {envVars} from '@/envVars.ts'
import {accountDeletionAxios} from '../api/accountDeletionAxios'

/**
 * Identity resolution. Reuses the existing `/users/me` endpoint, but called
 * through the feature's isolated axios instance with the bearer applied
 * on-the-fly from the token read out of the URL hash. A successful response
 * both authenticates the token and yields the fields we display read-only.
 */
export const DeletionAccountUser = z.object({
    fullName: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    email: z.string()
})
export type DeletionAccountUser = z.infer<typeof DeletionAccountUser>

export const httpGetDeletionUser = (token: string) =>
    accountDeletionAxios.get('/users/me', {
        params: {responseType: 'small'},
        headers: {Authorization: `Bearer ${token}`}
    })

export type RequestAccountDeletionPayload = {
    name: string
    email: string
    note?: string
}

export type RequestAccountDeletionResponse = {success: true}

/**
 * Submit the account deletion request.
 *
 * STOPGAP: the backend does not yet expose an endpoint, so the request is
 * appended to a Google Sheet via a Google Apps Script Web App. The shared
 * `secret` guards the (otherwise public) Apps Script URL against anonymous
 * spam — it is necessarily visible in the client bundle, so it only deters
 * casual abuse, not a determined attacker.
 *
 * Apps Script `ContentService` responses carry no CORS headers, so the browser
 * cannot read the response (and ContentService cannot add them). We therefore
 * send a `no-cors` "fire-and-forget" request: the row IS written to the Sheet,
 * but the response is opaque, so we cannot inspect a status/body. A request
 * that completes is treated as success; only a network failure rejects.
 *
 * Consequence: a wrong `secret` (script replies `forbidden`, no row written)
 * is NOT detectable client-side — verify the secret matches during setup.
 *
 * `text/plain` keeps it a CORS-safelisted "simple request" (no preflight).
 */
export const httpRequestAccountDeletion = async (
    payload: RequestAccountDeletionPayload
): Promise<RequestAccountDeletionResponse> => {
    await fetch(envVars.VITE_DELETION_SHEET_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: {'Content-Type': 'text/plain;charset=utf-8'},
        body: JSON.stringify({
            name: payload.name,
            email: payload.email,
            note: payload.note ?? '',
            secret: envVars.VITE_DELETION_SHEET_SECRET
        })
    })

    return {success: true}
}
